/**
 * 选科分班 Store
 *
 * 管理选科分班任务的状态和业务逻辑。
 *
 * @module stores/subject-assignment
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { SubjectAssignmentTask, SubjectAssignmentForm, SubjectAssignmentTaskUpdateRequest } from '@/types'
import { subjectAssignmentApi } from '@/api/subject-assignment'
import { useTaskOutcomeDismiss } from '@/composables/useTaskOutcomeDismiss'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'

export const useSubjectAssignmentStore = defineStore('subject-assignment', () => {
  const tasks = ref<SubjectAssignmentTask[]>([])
  const currentTaskId = ref<string | null>(null)
  const currentView = ref<'tasks' | 'detail'>('tasks')
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const { shouldShowOutcome, dismissTaskOutcome, acknowledgeTaskOutcome } =
    useTaskOutcomeDismiss('subjectAssignment.dismissedOutcomes')

  const currentTask = computed(() => {
    if (!currentTaskId.value) return null
    return tasks.value.find(task => task.id === currentTaskId.value) || null
  })

  async function fetchTasks(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      tasks.value = await subjectAssignmentApi.listTasks()
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      loading.value = false
    }
  }

  function selectTask(taskId: string): void {
    currentTaskId.value = taskId
    currentView.value = 'detail'
  }

  function backToTasks(): void {
    currentView.value = 'tasks'
    currentTaskId.value = null
  }

  async function createTask(form: SubjectAssignmentForm): Promise<SubjectAssignmentTask> {
    const task = await subjectAssignmentApi.createTask(form)
    tasks.value.unshift(task)
    return task
  }

  async function updateTask(taskId: string, updates: SubjectAssignmentTaskUpdateRequest): Promise<SubjectAssignmentTask | null> {
    const task = await subjectAssignmentApi.updateTask(taskId, updates)
    const index = tasks.value.findIndex(t => t.id === taskId)
    if (index > -1) {
      tasks.value[index] = task
    }
    return task
  }

  async function deleteTask(taskId: string): Promise<void> {
    await subjectAssignmentApi.removeTask(taskId)
    const index = tasks.value.findIndex(task => task.id === taskId)
    if (index > -1) {
      tasks.value.splice(index, 1)
    }
    if (currentTaskId.value === taskId) {
      backToTasks()
    }
  }

  async function executeAssignment(taskId: string): Promise<SubjectAssignmentTask> {
    const task = await subjectAssignmentApi.executeAssignment(taskId)
    const index = tasks.value.findIndex(t => t.id === taskId)
    if (index > -1) {
      tasks.value[index] = task
    }
    if (currentTaskId.value === taskId) {
      currentTaskId.value = null
      currentTaskId.value = taskId
    }
    return task
  }

  async function cancelQueued(taskId: string): Promise<SubjectAssignmentTask> {
    const task = await subjectAssignmentApi.cancelQueued(taskId)
    upsertTask(task)
    return task
  }

  async function stopRun(taskId: string): Promise<SubjectAssignmentTask> {
    const task = await subjectAssignmentApi.stopRun(taskId)
    upsertTask(task)
    return task
  }

  function upsertTask(task: SubjectAssignmentTask): void {
    const index = tasks.value.findIndex(t => t.id === task.id)
    if (index > -1) {
      tasks.value[index] = task
    } else {
      tasks.value.unshift(task)
    }
  }

  async function refreshCurrentTask(taskId: string): Promise<void> {
    try {
      const refreshed = await subjectAssignmentApi.getTask(taskId)
      if (refreshed) {
        upsertTask(refreshed)
      }
    } catch {
      // ignore
    }
  }

  /** 切换租户 / 登出时清空本 Store，防止跨租户任务数据残留。 */
  function resetAll(): void {
    tasks.value = []
    currentTaskId.value = null
    currentView.value = 'tasks'
    loading.value = false
    error.value = null
  }

  return {
    resetAll,
    tasks,
    currentTaskId,
    currentTask,
    currentView,
    loading,
    error,
    selectTask,
    backToTasks,
    createTask,
    updateTask,
    deleteTask,
    fetchTasks,
    executeAssignment,
    cancelQueued,
    stopRun,
    upsertTask,
    refreshCurrentTask,
    shouldShowOutcome,
    dismissTaskOutcome,
    acknowledgeTaskOutcome
  }
})

registerTenantScopedReset('subject-assignment', () => useSubjectAssignmentStore().resetAll())
